import { Alert, Flex, Heading, InlineList, Text, View } from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import SimpleTable from "../../../../../../sections/Cards/SimpleTable/SimpleTable";
import { ExternalService } from "../../../../../../services/ExternalService";
import ChartColors from "../../../../../../utils/ChartColors";
import { sort } from "../../../../../../utils/utils";
import { getOriginalOutcomeVersion } from "../../../../../LearningOutcomes/utils";
import "../resultTable.css";
import { transformResultsToTableData } from "../utils/transformResultsToTableData";
import { drawGraph } from "./utils";

const OverallChartData = ({
  measure,
  fullOutcomes,
  selectedSubject,
  triggerMap,
  filters,
  prevFilters,
  currentMode,
  finalizeFlag,
}) => {
  const { t } = useTranslation();
  const [loadData, setLoadData] = useState(false);
  const [loadingChart, setLoadingChart] = useState(true);
  const [error, setError] = useState(false);
  const [noData, setNoData] = useState(false);
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [updateChart, setUpdateChart] = useState(false);

  useEffect(() => {
    if (prevFilters.masteryResults !== filters.masteryResults) {
      setUpdateChart(true);
    } else {
      setUpdateChart(false);
    }
  }, [prevFilters, filters]);

  useEffect(() => {
    if (updateChart) {
      setLoadData(true);
    }
  }, [triggerMap]);

  useEffect(() => {
    setLoadData(true);
  }, [selectedSubject]);

  useEffect(() => {
    const getFlexibleGraphData = async () => {
      try {
        setLoadingChart(true);
        setTitle(
          filters.masteryResults === "true"
            ? t("assessment.masteryAvgOverall")
            : t("assessment.gradeAvgOverall")
        );

        const loType = measure.los[0].outcome_type;
        const outcomeIds = fullOutcomes.map(
          (lo) => getOriginalOutcomeVersion(lo).id
        );
        const mode = "bar";
        const terms = measure.terms.map((term) => term.canvas_id);
        const courseLevels = measure.course_levels;
        const masteryResults = filters.masteryResults;
        const categories = [];
        const context = null;
        const courseIds = [];
        const variety = measure.los[0].outcome_variety;
        const offeringIds = [];

        const graphData = await ExternalService.getDashboardResults(
          loType,
          outcomeIds,
          selectedSubject.title,
          mode,
          terms,
          courseLevels,
          masteryResults,
          categories,
          context,
          courseIds,
          variety,
          offeringIds,
          finalizeFlag
        );

        const results = graphData.results;

        if (results["ac_subject_results"][0].length > 0) {
          setNoData(false);
          const categories = results["ac_subject_results"][0];
          const jsonData =
            loType === "ISLO"
              ? getIsloJsonData(results, categories)
              : getPsloJsonData(results, categories);

          setData(jsonData);
        } else {
          setNoData(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoadingChart(false);
      }
    };
    if (loadData && selectedSubject && fullOutcomes) {
      getFlexibleGraphData();
      setLoadData(false);
    }
  }, [
    measure,
    loadData,
    filters.masteryResults,
    selectedSubject,
    fullOutcomes,
  ]);

  useEffect(() => {
    if (data && currentMode === "bar_chart") {
      const yAxisLabel =
        filters.masteryResults === "false"
          ? t("dashboard.avgAssignmentGrade")
          : t("dashboard.percentAchievedMastery");
      drawGraph("overall-chart-" + measure.id, yAxisLabel, data);
    }
  }, [data, measure.id, currentMode]);

  const getIsloJsonData = (res, categories) => {
    const json_data = [];
    const all_sub_key_name = res["all_subjects_results"][1][0];
    const ac_sub_key_name = res["ac_subject_results"][1][0];
    categories.forEach((category) => {
      const cat_index_all =
        res["all_subjects_results"][0].indexOf(category) + 1;
      const cat_index_ac = res["ac_subject_results"][0].indexOf(category) + 1;
      const all_sub_result = res["all_subjects_results"][1][cat_index_all];
      const ac_sub_result = res["ac_subject_results"][1][cat_index_ac];
      const category_json_entry = {
        name: category,
        [all_sub_key_name]:
          all_sub_result && cat_index_all
            ? Number(all_sub_result.toFixed(2))
            : all_sub_result,
        [ac_sub_key_name]:
          ac_sub_result && cat_index_ac
            ? Number(ac_sub_result.toFixed(2))
            : ac_sub_result,
      };
      json_data.push(category_json_entry);
    });
    return json_data;
  };

  const getPsloJsonData = (res, categories) => {
    const json_data = [];
    const ac_sub_key_name = res["ac_subject_results"][1][0];
    categories.forEach((category) => {
      const cat_index_ac = res["ac_subject_results"][0].indexOf(category) + 1;
      const ac_sub_result = res["ac_subject_results"][1][cat_index_ac];
      const all_sub_json_entry = {
        name: category,
        [ac_sub_key_name]:
          ac_sub_result && cat_index_ac
            ? Number(ac_sub_result.toFixed(2))
            : ac_sub_result,
      };
      json_data.push(all_sub_json_entry);
    });
    return json_data;
  };

  return loadingChart ? (
    <Flex.Item size="100%" padding="0 0 0 large">
      <Skeleton style={{ height: 300, borderRadius: 5 }} />
    </Flex.Item>
  ) : error ? (
    <Flex.Item size="100%" padding="0 0 0 large">
      <Alert variant="error">
        {t("notifications.general.fetchError", {
          obj: t("results.assessmentResults"),
        })}
      </Alert>
    </Flex.Item>
  ) : noData ? (
    <Flex.Item size="100%" padding="0 0 0 large">
      <Alert variant="info">{t("assessment.noResultsFound")}</Alert>
    </Flex.Item>
  ) : (
    <>
      {currentMode === "bar_chart" && (
        <>
          <Flex.Item size="100%" padding="small 0 0 large">
            <View textAlign="center">
              <Heading level="h4">{title}</Heading>
              <div
                id={"overall-chart-" + measure.id}
                style={{ margin: "10px 0 -20px 0", width: "100%" }}
              />
            </View>
          </Flex.Item>
          <Flex.Item size="100%" padding="0 0 medium xx-large">
            <View as="div" textAlign="center">
              <InlineList delimiter="pipe" isUnstyled>
                {sort(Object.keys(data[0]).filter((key) => key !== "name")).map(
                  (category, index) => (
                    <InlineList.Item key={index}>
                      <div
                        style={{
                          display: "inline-block",
                          height: 10,
                          width: 10,
                          marginRight: 8,
                          backgroundColor: ChartColors[index],
                        }}
                      />
                      <Text as="span" size="small">
                        {category}
                      </Text>
                    </InlineList.Item>
                  )
                )}
              </InlineList>
            </View>
          </Flex.Item>
        </>
      )}
      {currentMode === "table" && data && (
        <Flex.Item size="100%" padding="0 0 medium large">
          <div
            aria-live="polite"
            aria-atomic="true"
            className="result-table-class"
          >
            {(() => {
              const categories = sort(
                Object.keys(data[0]).filter((key) => key !== "name")
              );
              const tableProps = transformResultsToTableData(
                [data, categories],
                title
              );
              return (
                <SimpleTable
                  data={tableProps.data}
                  caption={<Text weight="bold">{tableProps.caption}</Text>}
                  rowTitle={tableProps.rowTitle}
                />
              );
            })()}
          </div>
        </Flex.Item>
      )}
    </>
  );
};

export default OverallChartData;
